import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card } from '@mui/material';
import { ProgressBar } from './ProgressBar';

export const PodiumCarousel = ({ data }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [delay, setDelay] = useState(5000);

    const getIngreso = (item) => item.Ingreso_anual ?? item.ingreso_Anual ?? item.ingresoAnual ?? item.ingresosAcumulados ?? 0;
    const getMeta = (item) => item.Meta_Anual ?? item.meta_Anual ?? item.metaAnual ?? 1;
    const getVistas = (item) => item.Visualizaciones_propiedades ?? item.Visualizaciones_Propiedades ?? item.visualizaciones_Propiedades ?? item.visualizacionesPropiedades ?? item.visualizaciones ?? 0;
    const getStock = (item) => {
        const stock = item.N_Stocks ?? item.n_Stocks ?? item.nStocks ?? item.nStock ?? 1;
        return stock === 0 ? 1 : stock;
    };
    const getCierres = (item) => item.N_Cierres_Anual ?? item.n_Cierres_Anual ?? item.nCierresAnual ?? item.cierresAnuales ?? 0;

    const metrics = [
        {
            label: "TOP AVANCE ANUAL",
            key: "avanceAnual",
            calculate: (item) => (getIngreso(item) / (getMeta(item) || 1)) * 100,
            suffix: "%",
            format: (v) => v.toFixed(1),
            maxValue: 100
        },
        {
            label: "RANKING POR VISUALIZACIONES",
            key: "visualizaciones",
            calculate: (item) => getVistas(item),
            suffix: " Vistas",
            format: (v) => Math.round(v),
            maxValue: Math.max(1, ...data.map(d => getVistas(d)))
        },
        {
            label: "VISUALIZACIONES POR PROPIEDAD (PROM.)",
            key: "avgViews",
            calculate: (item) => getVistas(item) / getStock(item),
            suffix: " Vistas/Prop",
            format: (v) => v.toFixed(1),
            maxValue: Math.max(1, ...data.map(d => getVistas(d) / getStock(d)))
        },
        {
            label: "RANKING DE CIERRES",
            key: "cierresAnuales",
            calculate: (item) => getCierres(item),
            suffix: " Cierres",
            format: (v) => Math.round(v),
            maxValue: Math.max(1, ...data.map(d => getCierres(d)))
        }
    ];

    useEffect(() => {
        const timer = setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % metrics.length);
            setDelay(5000);
        }, delay);
        return () => clearTimeout(timer);
    }, [currentIndex, delay, metrics.length]);

    const handleSelect = (idx) => {
        setCurrentIndex(idx);
        setDelay(10000);
    };

    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        
        if (isLeftSwipe) {
            handleSelect(currentIndex === metrics.length - 1 ? 0 : currentIndex + 1);
        } else if (isRightSwipe) {
            handleSelect(currentIndex === 0 ? metrics.length - 1 : currentIndex - 1);
        }
    };

    const activeMetric = metrics[currentIndex];

    const sortedData = [...data].sort((a, b) => {
        const valA = activeMetric.calculate ? activeMetric.calculate(a) : a[activeMetric.key];
        const valB = activeMetric.calculate ? activeMetric.calculate(b) : b[activeMetric.key];
        return valB - valA;
    });

    const top1 = sortedData[0];
    const top2 = sortedData[1];
    const top3 = sortedData[2];

    const renderPodiumCard = (broker, rank, isTop1 = false) => {
        if (!broker) return null;

        const height = isTop1 ? 140 : 120;
        const value = activeMetric.calculate ? activeMetric.calculate(broker) : broker[activeMetric.key];
        const percent = (value / activeMetric.maxValue) * 100;

        const rankColor = rank === 1 ? "#00a62b" : rank === 2 ? "#008d24" : "#a0a0a0";

        return (
            <Card
                elevation={1}
                sx={{
                    bgcolor: 'white',
                    color: 'text.primary',
                    textAlign: 'left',
                    p: 2,
                    borderRadius: 2,
                    height: height,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: isTop1 ? '2px solid #00a62b' : '1px solid #e0e0e0',
                    transition: 'all 0.3s ease-in-out',
                    position: 'relative'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: "bold",
                                color: rankColor,
                                lineHeight: 1,
                                mb: 0.5
                            }}
                        >
                            #{rank}
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 'bold',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                fontSize: { xs: '0.65rem', sm: '0.875rem' },
                                lineHeight: 1.2
                            }}
                        >
                            {broker.nombre_Broker}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ width: "100%", mt: 1 }}>
                    <ProgressBar
                        value={percent}
                        hexColor={rankColor}
                        suffix={activeMetric.suffix}
                        displayValue={activeMetric.format(value)}
                    />
                </Box>
            </Card>
        );
    };

    return (
        <Box 
            sx={{ mb: 4, width: '100%' }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 900,
                        textAlign: 'center',
                        color: '#333',
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        position: 'relative',
                        display: 'inline-block',
                        '&:after': {
                            content: '""',
                            position: 'absolute',
                            bottom: -4,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 30,
                            height: 2,
                            borderRadius: 1
                        }
                    }}
                >
                    {activeMetric.label}
                </Typography>
            </Box>

            <Grid container spacing={2} sx={{ alignItems: 'flex-end', justifyContent: 'center' }}>
                <Grid item xs={4}>
                    {renderPodiumCard(top2, 2)}
                </Grid>
                <Grid item xs={4}>
                    {renderPodiumCard(top1, 1, true)}
                </Grid>
                <Grid item xs={4}>
                    {renderPodiumCard(top3, 3)}
                </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pb: 1, gap: 1 }}>
                {metrics.map((_, idx) => (
                    <Box
                        key={idx}
                        onClick={() => handleSelect(idx)}
                        sx={{
                            width: idx === currentIndex ? 16 : 8,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: idx === currentIndex ? '#00a62b' : '#ccc',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
};
